import { uniqueArray } from '../../../utilities'

// Resets to starting state
export const handleReset = () => {
  return initialState
}

// Sets the current list
export const handleSetList = (state, action) => {
  //* list: string[] - IDs to set
  //* current: string? - ID in the list to make the current
  //* less?: () => void - callback that is called when the first item is selected
  //* more?: () => void - callback that is called when the last item is selected
  const { list = [], current, less, more } = action

  // Remove duplicates and invalid values
  const taskIDs = uniqueArray(list).filter(Boolean)
  const hasTasks = taskIDs.length > 0

  // Default current idx and task
  let currentIdx = 0
  let currentTask = hasTasks ? taskIDs[0] : null

  // Update currents if a current is given
  if (current && taskIDs.includes(current)) {
    currentIdx = taskIDs.findIndex(val => val === current)
    currentTask = current
  }

  // Scrollable if not at either end
  const canScrollRight = hasTasks && currentIdx < taskIDs.length - 1
  const canScrollLeft = hasTasks && currentIdx > 0

  if (currentIdx === 0) {
    if (state.stack.length === 0) {
      setTimeout(() => {
        less && less()
      }, 100)
    }
  }

  if (currentIdx === taskIDs.length - 1) {
    if (state.stack.length === 0) {
      setTimeout(() => {
        more && more()
      }, 100)
    }
  }

  // Update current state
  const newCurrent = {
    ...state.current,
    taskIDs,
    canScrollLeft,
    canScrollRight,
    currentIdx,
    currentTask
  }

  return { ...state, current: newCurrent }
}

// Moves forward in the list
export const handleNext = (state, action) => {
  //* cb?: (x: string) => void - optional function, takes a callback that is called with the new task
  //* more?: () => void - callback that is called when the last item is selected
  const { cb, more } = action

  // Do nothing if on the last element
  if (!state.current.canScrollRight) {
    return state
  }

  // Increment and update
  const currentIdx = state.current.currentIdx + 1
  const currentTask = state.current.taskIDs[currentIdx]
  cb && cb(currentTask)

  const canScrollLeft = currentIdx > 0
  const canScrollRight = currentIdx < state.current.taskIDs.length - 1
  if (currentIdx === state.current.taskIDs.length - 1) {
    if (state.stack.length === 0) {
      setTimeout(() => {
        more && more()
      }, 100)
    }
  }

  // Update current state
  const newCurrent = {
    ...state.current,
    canScrollLeft,
    canScrollRight,
    currentIdx,
    currentTask
  }

  return { ...state, current: newCurrent }
}

// Moves back in the list
export const handlePrev = (state, action) => {
  //* cb?: (x: string) => void - optional function, takes a callback that is called with the new task
  //* less?: () => void - callback that is called when the first item is selected
  const { cb, less } = action

  if (!state.current.canScrollLeft) {
    return state
  }

  // Decrement and update
  const currentIdx = state.current.currentIdx - 1
  const currentTask = state.current.taskIDs[currentIdx]
  cb && cb(currentTask)

  const canScrollLeft = currentIdx > 0
  const canScrollRight = currentIdx < state.current.taskIDs.length - 1
  if (currentIdx === 0) {
    if (state.stack.length === 0) {
      setTimeout(() => {
        less && less()
      }, 100)
    }
  }

  // Update current state
  const newCurrent = {
    ...state.current,
    canScrollLeft,
    canScrollRight,
    currentIdx,
    currentTask
  }

  return { ...state, current: newCurrent }
}

// Adds tasks to the end of the list
export const handleAddTasks = (state, action) => {
  //* tasks: string[] - new ids to add to the end of the list
  const { tasks } = action

  // Join lists
  const list = [...state.current.taskIDs, ...tasks]
  const current = state.current.currentTask

  // Set state based on joined list and current
  return handleSetList(state, { list, current })
}

// Adds tasks to the front of the list
export const handleAddTasksFront = (state, action) => {
  //* tasks: string[] - new ids to add to the end of the list
  const { tasks } = action

  // Join lists
  const list = [...tasks, ...state.current.taskIDs]
  const current = state.current.currentTask

  // Set state based on joined list and current
  return handleSetList(state, { list, current })
}

// Stores a state on the stack
export const handlePushFrame = (state, action) => {
  // Store current state
  const stack = [...state.stack, { ...state.current }]

  // Reset state
  const current = { ...initialState.current }

  // Update state
  const newState = { ...state, stack, current }

  // Set current based on action
  return handleSetList(newState, action)
}

// Restores a state from the stack
export const handlePopFrame = (state, action) => {
  //* cb?: (x:string) => void - Optional callback that is called with the restored current task
  const { cb } = action

  // Nothing to restore
  if (state.stack.length === 0) {
    return state
  }

  // Restore state from the last stored entry
  const stack = [...state.stack]
  const current = { ...stack.pop() }

  if (current.currentTask) {
    cb && cb(current.currentTask)
  }

  return { stack, current }
}

export const initialState = {
  stack: [],
  current: {
    taskIDs: [],
    canScrollLeft: false,
    canScrollRight: false,
    currentIdx: 0,
    currentTask: null
  }
}

export const Actions = {
  RESET: 'RESET',
  SET_LIST: 'SET_LIST',
  NEXT: 'NEXT',
  PREV: 'PREV',
  ADD_TASKS: 'ADD_TASKS',
  ADD_TASKS_FRONT: 'ADD_TASKS_FRONT',
  PUSH_FRAME: 'PUSH_FRAME',
  POP_FRAME: 'POP_FRAME'
}

const Handlers = {
  [Actions.RESET]: handleReset,
  [Actions.SET_LIST]: handleSetList,
  [Actions.NEXT]: handleNext,
  [Actions.PREV]: handlePrev,
  [Actions.ADD_TASKS]: handleAddTasks,
  [Actions.ADD_TASKS_FRONT]: handleAddTasksFront,
  [Actions.PUSH_FRAME]: handlePushFrame,
  [Actions.POP_FRAME]: handlePopFrame
}

export const TaskListReducer = (state, action) => {
  const { type } = action

  if (type in Handlers) {
    return Handlers[type](state, action)
  }

  throw new Error(`Unknown TaskList reducer type: ${type}`)
}
